"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from "react";
import { cn } from "../../utils/index.js";
import { ContentfulImgOptimizer } from "../ContentfulImgOptimizer/ContentfulImgOptimizer.js";
import { WidthCapContext } from "../ContentfulImgOptimizer/WidthCapContext.js";
import { Image } from "../Image/index.js";
import { Scrim } from "../Scrim/index.js";
import { kenBurnsEffectClassMap } from "../animations/KenBurnsEffect/kenBurnsEffectClasses.js";
import { stageVariants } from "./Stage.variants.js";
const KEN_BURNS_CENTER_LONG = kenBurnsEffectClassMap.find((effect)=>'Center Long' === effect.name);
function Stage({ header, body, className, palette, emphasis, media = false, image, imageRole, imageHref, onImageClick, imageAnchorProps, imageButtonProps, imageAriaLabel, ...restProps }) {
    const isImageActionable = media && !!imageRole;
    const [isHovered, setIsHovered] = useState(false);
    const handleMouseEnter = useCallback(()=>setIsHovered(true), []);
    const handleMouseLeave = useCallback(()=>setIsHovered(false), []);
    const kenBurnsDirection = isHovered ? KEN_BURNS_CENTER_LONG?.in : KEN_BURNS_CENTER_LONG?.out;
    const kenBurnsClass = isImageActionable ? kenBurnsDirection : void 0;
    const { stageContainerSlot, headingSlot, imageSlot, imageClickableSlot, bodyOverlaySlot, innerContentSlot } = stageVariants({
        palette,
        emphasis,
        media,
        imageActionable: isImageActionable
    });
    const containerClass = cn(stageContainerSlot(), className);
    const renderHeader = !!header;
    const showImage = media && !!image;
    const imageElement = useMemo(()=>{
        if (!showImage || !image) return null;
        if ('contentfulUrl' in image) return /*#__PURE__*/ jsx(ContentfulImgOptimizer, {
            contentfulUrl: image.contentfulUrl,
            alt: image.alt,
            width: "full",
            height: "full",
            objectFit: "cover"
        });
        return /*#__PURE__*/ jsx(Image, {
            src: image.src,
            alt: image.alt,
            width: "full",
            height: "full",
            objectFit: "cover"
        });
    }, [
        image,
        showImage
    ]);
    const headerNode = renderHeader ? /*#__PURE__*/ jsx("div", {
        className: headingSlot(),
        "data-testid": "stage-header",
        children: header
    }) : null;
    if (media) {
        const mediaContainerContent = /*#__PURE__*/ jsxs("div", {
            className: containerClass,
            "data-testid": "stage-container",
            children: [
                showImage && /*#__PURE__*/ jsx(WidthCapContext.Provider, {
                    value: "container",
                    children: /*#__PURE__*/ jsx("div", {
                        className: cn(imageSlot(), kenBurnsClass),
                        "data-testid": "stage-image",
                        children: imageElement
                    })
                }),
                renderHeader && /*#__PURE__*/ jsx(Scrim, {
                    isBottom: true,
                    padding: 3,
                    overhang: 16,
                    className: "absolute inset-x-0 bottom-0",
                    children: /*#__PURE__*/ jsx("div", {
                        className: headingSlot(),
                        "data-testid": "stage-header",
                        children: header
                    })
                })
            ]
        });
        const renderImageClickable = ()=>{
            if (!isImageActionable) return mediaContainerContent;
            if ('link' === imageRole) return /*#__PURE__*/ jsx("a", {
                "aria-label": imageAriaLabel,
                href: imageHref,
                onClick: onImageClick,
                ...imageAnchorProps,
                className: imageClickableSlot(),
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                children: mediaContainerContent
            });
            return /*#__PURE__*/ jsx("button", {
                "aria-label": imageAriaLabel,
                type: "button",
                onClick: onImageClick,
                ...imageButtonProps,
                className: imageClickableSlot(),
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                children: mediaContainerContent
            });
        };
        return /*#__PURE__*/ jsxs("div", {
            className: "@container relative",
            ...restProps,
            children: [
                renderImageClickable(),
                body && /*#__PURE__*/ jsx("div", {
                    className: innerContentSlot(),
                    children: /*#__PURE__*/ jsx("div", {
                        className: bodyOverlaySlot(),
                        "data-testid": "stage-body-overlay",
                        children: body
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ jsxs("div", {
        className: "relative",
        ...restProps,
        children: [
            /*#__PURE__*/ jsx("div", {
                className: containerClass,
                "data-testid": "stage-container"
            }),
            /*#__PURE__*/ jsxs("div", {
                className: "relative",
                children: [
                    headerNode,
                    /*#__PURE__*/ jsx("div", {
                        className: "mx-3",
                        children: body
                    })
                ]
            })
        ]
    });
}
export { Stage };
